import { useEffect, useRef, useState } from "react";
import { TYPE_LABEL, OBS_TYPES, go, entityPath, ipPath, getJson, IdBadge, ConfidenceBadge, KevBadge, RULE_TYPE, RULE_ENGINE, SANCTION_BODY } from "./kb/shared";

// One search box for the whole site, VirusTotal-style: a group, malware, tool,
// technique, or a pasted IP / domain / URL / hash. Opens from the sidebar, or
// with Ctrl+K (⌘K) or "/" from anywhere that isn't a text field.

const EXAMPLES = ["APT28", "LockBit", "Cobalt Strike", "T1059", "Mirai"];
const BROWSE = [
  { l: "Reports", h: "#/kb/reports" },
  { l: "Threat groups", h: "#/kb/groups" },
  { l: "Malware", h: "#/kb/malware" },
  { l: "Techniques", h: "#/kb/techniques" },
  { l: "IP addresses", h: "#/kb/ips" },
  { l: "Dashboards", h: "#/dash" },
];
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
const LOOKS_LIKE_INDICATOR = /^(\d{1,3}(\.\d{1,3}){3}|[a-f0-9]{32,64}|https?:\/\/\S+|[a-z0-9-]+(\.[a-z0-9-]+)+)$/i;

export function isTyping(e) {
  const t = e.target;
  return t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName));
}

// Anything on the page can open the palette without holding its state.
export const openGlobalSearch = () => window.dispatchEvent(new Event("ih-open-search"));

export function useSearchHotkey(open) {
  useEffect(() => {
    window.addEventListener("ih-open-search", open);
    const onKey = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.ctrlKey || e.metaKey)) { e.preventDefault(); open(); }
      else if (e.key === "/" && !isTyping(e) && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); open(); }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("ih-open-search", open); };
  }, [open]);
}

export function SearchPalette({ onClose, ui }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [searched, setSearched] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const term = q.trim();
    if (!term) { setItems([]); setSearched(""); return; }
    const t = setTimeout(() => {
      getJson(`/api/kb/search?q=${encodeURIComponent(term)}`)
        .then(d => { setItems(d.items || []); setActive(0); setSearched(term); })
        .catch(() => { setItems([]); setSearched(term); });
    }, 150);
    return () => clearTimeout(t);
  }, [q]);

  const visit = (hash) => { onClose(); go(hash.replace(/^#/, "")); };
  const pick = (s) => visit(entityPath(s));
  const term = q.trim();

  return (
    <div onMouseDown={onClose} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.55)", display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "12vh 16px 16px" }}>
      <div role="dialog" aria-label="Search" onMouseDown={e => e.stopPropagation()}
        style={{ width: "min(680px, 100%)", background: B.bgPanel, border: `1px solid ${B.borderLight}`, borderRadius: 14, boxShadow: "0 24px 64px rgba(0,0,0,0.45)", overflow: "hidden", fontFamily: "'Satoshi', sans-serif" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 16px", borderBottom: `1px solid ${B.border}` }}>
          <SearchGlyph color={B.textDim} />
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => {
              if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, items.length - 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
              else if (e.key === "Enter" && items[active]) pick(items[active]);
              else if (e.key === "Enter" && IPV4.test(q.trim())) visit(ipPath(q.trim()));
              else if (e.key === "Escape") onClose();
            }}
            placeholder="Search a group, malware, technique, or paste an IP, domain, URL or hash"
            aria-label="Search"
            style={{ flex: 1, minWidth: 0, background: "none", border: "none", outline: "none", color: B.text, fontSize: fs(15), padding: "14px 0", fontFamily: "inherit" }}
          />
          <kbd style={kbd(ui)}>Esc</kbd>
        </div>

        <div style={{ maxHeight: "56vh", overflowY: "auto" }}>
          {!term && (
            <div style={{ padding: "14px 16px 16px" }}>
              <div style={hint(ui)}>Try</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                {EXAMPLES.map(x => (
                  <button key={x} onClick={() => setQ(x)} style={{ background: B.bgCard, border: `1px solid ${B.border}`, color: B.text, borderRadius: 999, padding: "5px 12px", cursor: "pointer", fontSize: fs(12), fontFamily: "inherit" }}>{x}</button>
                ))}
              </div>
              <div style={hint(ui)}>Browse</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                {BROWSE.map(b => (
                  <button key={b.h} onClick={() => visit(b.h)} style={{ background: "none", border: "none", padding: 0, color: B.accent, cursor: "pointer", fontSize: fs(13), fontFamily: "inherit" }}>{b.l} →</button>
                ))}
              </div>
            </div>
          )}

          {term && searched === term && items.length === 0 && (
            <div style={{ padding: 16, fontSize: fs(13), color: B.textMuted, lineHeight: 1.6 }}>
              No matches for <b style={{ color: B.text }}>{term}</b>.
              {LOOKS_LIKE_INDICATOR.test(term)
                ? " Indicators show up here while they're among the newest entries in the ThreatFox, URLhaus or MalwareBazaar feeds. Not being listed doesn't mean it's safe."
                : " Try a group's other name, a malware family, or an ATT&CK ID like T1566."}
            </div>
          )}

          {IPV4.test(term) && (
            <button onMouseDown={() => visit(ipPath(term))} style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", borderBottom: `1px solid ${B.border}`, padding: "10px 16px", cursor: "pointer", fontSize: fs(13), color: B.accent, fontFamily: "inherit" }}>
              Look up <b style={{ fontFamily: "monospace" }}>{term}</b> in Internet Watch: open ports, scanning, blocklists
            </button>
          )}

          {items.map((s, i) => {
            const isObs = OBS_TYPES.has(s.type) || s.kind === "wallet";
            const money = s.kind === "wallet" || s.kind === "sanctioned" || s.kind === "sanction" || s.kind === "package" || s.kind === "listing";
            return (
              <div key={s.id} onMouseDown={() => pick(s)} onMouseEnter={() => setActive(i)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", cursor: "pointer", background: i === active ? B.primary + "18" : "transparent", borderTop: i ? `1px solid ${B.border}` : "none" }}>
                <TypeIcon type={s.type} color={getConf(s.type).color} size={15} />
                <span style={{ flex: 1, minWidth: 0, fontSize: fs(isObs ? 12 : 14), fontFamily: isObs ? "monospace" : "inherit", color: B.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.name}{s.parentName ? <span style={{ color: B.textDim, fontSize: fs(11) }}> · {s.parentName}</span> : null}
                  {!isObs && s.aliases?.length > 0 && <span style={{ color: B.textDim, fontSize: fs(11) }}> · {s.aliases.slice(0, 3).join(", ")}</span>}
                  {isObs && s.families?.length > 0 && <span style={{ color: B.textDim, fontSize: fs(11), fontFamily: "'Satoshi', sans-serif" }}> · {s.families.map(f => f.name).join(", ")}</span>}
                  {money && s.snippet && <span style={{ color: B.textDim, fontSize: fs(11), fontFamily: "'Satoshi', sans-serif" }}> · {s.snippet}</span>}
                </span>
                <KevBadge kev={s.kev} ui={ui} />
                <span className="ih-search-type" style={{ fontSize: fs(11), color: B.textDim, whiteSpace: "nowrap" }}>{s.type === RULE_TYPE ? `${RULE_ENGINE[s.engine]?.label} rule` : s.kind === "victim" ? `Victim of ${s.groupName}` : s.kind === "sanctioned" ? "Sanctioned (OFAC)" : s.kind === "sanction" ? `Sanctioned (${SANCTION_BODY[s.body] || s.body})` : s.kind === "package" ? `Malicious ${s.ecosystem} package` : s.kind === "listing" ? (s.hub === "prolif" ? "Sanctions listing" : "Export-control listing") : s.kind === "space-incident" ? "Space incident" : TYPE_LABEL[s.type]}</span>
                {isObs ? s.confidence && <ConfidenceBadge confidence={s.confidence} ui={ui} /> : <IdBadge id={s.attackId} ui={ui} />}
              </div>
            );
          })}
        </div>

        {items.length > 0 && (
          <div style={{ display: "flex", gap: 14, padding: "8px 16px", borderTop: `1px solid ${B.border}`, fontSize: fs(11), color: B.textDim }}>
            <span><kbd style={kbd(ui)}>↑</kbd> <kbd style={kbd(ui)}>↓</kbd> to move</span>
            <span><kbd style={kbd(ui)}>Enter</kbd> to open</span>
          </div>
        )}
      </div>
      <style>{`@media (max-width: 520px) { .ih-search-type { display: none; } }`}</style>
    </div>
  );
}

// The sidebar's entry point: a search field when expanded, an icon when collapsed.
export function SearchLauncher({ collapsed, onOpen, ui }) {
  const { B, fs } = ui;
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
  if (collapsed) {
    return (
      <button onClick={onOpen} title={`Search (${mac ? "⌘" : "Ctrl+"}K)`} aria-label="Search"
        style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", border: "none", borderRadius: 6, cursor: "pointer", background: "transparent" }}>
        <SearchGlyph color={B.textDim} />
      </button>
    );
  }
  return (
    <button onClick={onOpen} aria-label="Search"
      style={{ display: "flex", alignItems: "center", gap: 8, width: "calc(100% - 20px)", margin: "10px 10px 4px", padding: "8px 10px", background: B.bgCard, border: `1px solid ${B.border}`, borderRadius: 8, color: B.textDim, cursor: "pointer", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif", textAlign: "left" }}>
      <SearchGlyph color={B.textDim} />
      <span style={{ flex: 1 }}>Search anything…</span>
      <kbd style={kbd(ui)}>{mac ? "⌘" : "Ctrl"} K</kbd>
    </button>
  );
}

function SearchGlyph({ color }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

const kbd = ({ B, fs }) => ({ fontSize: fs(10), color: B.textDim, border: `1px solid ${B.border}`, borderRadius: 4, padding: "1px 5px", fontFamily: "inherit", background: B.bg });
const hint = ({ B, fs }) => ({ fontSize: fs(11), color: B.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, marginBottom: 8 });
